import React from "react";
import PropTypes from "prop-types";

/**
 * ChatWidget
 * ---------------------------------------------------------------------------
 * The expanded widget panel. Presentational only — receives `messages` and
 * `quickActions` as data, and reports intent (send, quick action, close)
 * upward via callback props so it can be dropped into any parent flow
 * (e.g. later wired to sockets/REST for live conversations).
 */
export default function ChatWidget({
  isOpen,
  botName = "ChatBiz AI",
  statusLabel = "Online",
  messages = [],
  quickActions = [],
  draft = "",
  onDraftChange,
  onSend,
  onQuickAction = () => {},
  isTyping = false,
  onClose,
  panelRef,
  showLeadCapture = false,
  onOpenLeadCapture = () => {},
  leadName = "",
  leadEmail = "",
  leadMessage = "",
  onLeadNameChange = () => {},
  onLeadEmailChange = () => {},
  onLeadSubmit = () => {},
  onCloseLeadCapture = () => {},
  leadConfig = { phone: "optional", company: false, message: false },
  leadExtra = { phone: "", company: "", message: "" },
  onLeadExtraChange = () => {},
  avatar = "",
  logo = "",
  welcomeMessage = "",
  panelWidth = 360,
  panelHeight = 520,
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (draft.trim().length === 0) return;
    onSend(draft.trim());
  };

  return (
    <div
      id="cbai-widget-panel"
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={`${botName} chat window`}
      className={`cbai-panel card shadow-lg${isOpen ? " cbai-panel--open" : " cbai-panel--closed"}`} style={{ width: panelWidth, height: panelHeight }}
    >
      {/* Header */}
      <div className="cbai-panel__header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <span className="cbai-avatar" aria-hidden="true">
            {avatar ? <img src={avatar} alt="" /> : logo ? <img src={logo} alt="" /> : "🤖"}
          </span>
          <div>
            <div className="cbai-panel__title">{botName}</div>
            <div className="cbai-panel__status">
              <span className="cbai-status-dot" aria-hidden="true" />
              {statusLabel}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="cbai-close-btn"
          aria-label="Close chat"
          onClick={onClose}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Message list */}
      <div className="cbai-panel__body">
        {messages.length === 0 && welcomeMessage && (
          <div className="cbai-message cbai-message--bot">
            <div className="cbai-bubble">{welcomeMessage}</div>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`cbai-message cbai-message--${m.from === "visitor" ? "visitor" : "bot"}`}
          >
            {m.label && <div className="cbai-timestamp" style={{ marginBottom: 2, fontWeight: 600 }}>{m.label}</div>}
            <div className="cbai-bubble">{m.text}</div>
            <div className="cbai-timestamp">{m.time}</div>
          </div>
        ))}

        {isTyping && (
          <div className="cbai-message cbai-message--bot" aria-live="polite">
            <div className="cbai-bubble cbai-typing-bubble" aria-label="Assistant is typing">
              <span />
              <span />
              <span />
            </div>
            <div className="cbai-timestamp">Assistant is typing…</div>
          </div>
        )}

        {quickActions && quickActions.length > 0 && (
          <div className="cbai-quick-actions">
            {quickActions.map((qa) => (
              <button
                key={qa.id}
                type="button"
                className="btn btn-sm cbai-quick-btn"
                onClick={() => onQuickAction(qa)}
                disabled={isTyping}
              >
                {qa.label}
              </button>
            ))}
          </div>
        )}

        {!showLeadCapture && (
          <button type="button" className="cbai-lead-link" onClick={onOpenLeadCapture} disabled={isTyping}>
            Share your name &amp; email
          </button>
        )}

        {showLeadCapture && (
          <form className="cbai-lead-form" onSubmit={onLeadSubmit}>
            <div className="cbai-lead-form__title">Share your contact details</div>
            <input type="text" value={leadName} onChange={(e) => onLeadNameChange(e.target.value)} placeholder="Your name" aria-label="Your name" />
            <input type="email" value={leadEmail} onChange={(e) => onLeadEmailChange(e.target.value)} placeholder="Email address" aria-label="Email address" />
            {leadConfig.phone !== "off" && (
              <input type="tel" inputMode="tel" value={leadExtra.phone} onChange={(e) => onLeadExtraChange("phone", e.target.value)}
                placeholder={leadConfig.phone === "required" ? "Phone number" : "Phone number (optional)"} aria-label="Phone number" aria-required={leadConfig.phone === "required"} />
            )}
            {leadConfig.company && (
              <input type="text" value={leadExtra.company} onChange={(e) => onLeadExtraChange("company", e.target.value)} placeholder="Company (optional)" aria-label="Company" />
            )}
            {leadConfig.message && (
              <textarea rows={2} value={leadExtra.message} onChange={(e) => onLeadExtraChange("message", e.target.value)} placeholder="How can we help? (optional)" aria-label="Message" />
            )}
            <div className="cbai-lead-form__actions">
              <button type="submit" className="cbai-lead-save">Save details</button>
              <button type="button" className="cbai-lead-cancel" onClick={onCloseLeadCapture}>Cancel</button>
            </div>
            {leadMessage && <div className={`cbai-lead-message ${leadMessage.includes("successfully") ? "success" : "error"}`}>{leadMessage}</div>}
          </form>
        )}
      </div>

      {/* Composer */}
      <form className="cbai-panel__footer" onSubmit={handleSubmit}>
        <input
          type="text"
          className="form-control cbai-input"
          placeholder="Type your message..."
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          aria-label="Type your message"
        />
        <button
          type="submit"
          className="cbai-send-btn"
          aria-label="Send message"
          disabled={draft.trim().length === 0 || isTyping}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 11.5 20.5 4l-6.7 17.5-3.2-7.1L3 11.5Z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
      <div className="cbai-panel__powered">Powered by ChatBiz AI</div>
    </div>
  );
}

ChatWidget.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  botName: PropTypes.string,
  statusLabel: PropTypes.string,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      from: PropTypes.oneOf(["visitor", "bot"]).isRequired,
      text: PropTypes.string.isRequired,
      time: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  quickActions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  draft: PropTypes.string,
  onDraftChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onQuickAction: PropTypes.func,
  isTyping: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  panelRef: PropTypes.shape({ current: PropTypes.any }),
  showLeadCapture: PropTypes.bool,
  onOpenLeadCapture: PropTypes.func,
  leadName: PropTypes.string,
  leadEmail: PropTypes.string,
  leadMessage: PropTypes.string,
  onLeadNameChange: PropTypes.func,
  onLeadEmailChange: PropTypes.func,
  onLeadSubmit: PropTypes.func,
  onCloseLeadCapture: PropTypes.func,
  leadConfig: PropTypes.shape({ phone: PropTypes.string, company: PropTypes.bool, message: PropTypes.bool }),
  leadExtra: PropTypes.shape({ phone: PropTypes.string, company: PropTypes.string, message: PropTypes.string }),
  onLeadExtraChange: PropTypes.func,
};
